import { getFirestore, doc, onSnapshot, Unsubscribe, FirestoreError } from 'firebase/firestore'
import { getApp } from 'firebase/app'
import store from '@/store'
import config from '@/config'

let unsubscribe: Unsubscribe | null = null
let listeningUserId: string | null = null

// 事件去重：用服务端写入的 createTime（每次写都刷新的 serverTimestamp）作为单调键，
// 持久化到 localStorage，使「刷新页面重读旧文档」不会重复触发，而「冷启动首次」与
// 「离开期间发生的新事件」仍会被处理一次。
//
// ⚠️ key 带 `host:` 命名空间：主站应用（本文件）与子站 SDK（sovi-auth-sdk.js）若同源，
//    localStorage 共享。两者是独立消费者（写 Vuex vs 回调集成方），各自要对每个事件处理一次，
//    游标必须隔离，否则一方写了 key 会让另一方误判「已处理」而漏刷新。
const LAST_SEEN_KEY_PREFIX = 'sovi:firestore:websparks:lastseen:host:'

function readLastSeen(userId: string): number {
    try {
        const v = localStorage.getItem(LAST_SEEN_KEY_PREFIX + userId)
        return v ? Number(v) || 0 : 0
    } catch {
        return 0
    }
}

function writeLastSeen(userId: string, millis: number): void {
    try {
        localStorage.setItem(LAST_SEEN_KEY_PREFIX + userId, String(millis))
    } catch {
        /* localStorage 不可用时退化为内存去重，刷新会重置 */
    }
}

function eventMillis(data: any): number | null {
    const t = data && data.createTime
    if (!t) return null
    if (typeof t.toMillis === 'function') return t.toMillis()
    if (typeof t.seconds === 'number') return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6)
    if (typeof t === 'number') return t
    return null
}

/**
 * 启动 Firestore Sparks 实时监听
 *
 * ⚠️ 与共享 SDK（public/shared/sovi-auth-sdk.js 的 bindSparks）刻意保持两套实现，不要合并：
 * - 本文件是「主站应用内」版本：用打包进 bundle 的 `firebase/firestore` npm 依赖，写 Vuex，
 *   并在权限/配置异常时降级到后端 API 兜底刷新（SDK 版本没有这层兜底）。
 * - SDK 版本是「子站跨域」版本：动态 import `/shared/` 下的 Firebase，走回调而非 Vuex。
 * 两者都只负责「读」，扣费一律走后端，二者在读取契约上必须保持一致。
 *
 * ⚠️ 依赖配置：
 * 1. Firebase 安全规则必须允许已认证用户（或对应 App Check 规则）读取目标集合。
 *    本项目未集成 firebase/auth，请确保 Firestore 控制台已将集合配置为允许前端读取。
 * 2. 后端在 sparks 扣减/发放后会写入 {firestoreCollectionName}/{userId}
 *
 * @param userId 当前用户ID
 */
export function startFirestoreListener(userId: string) {
    if (!userId) {
        console.warn('[主站][FirestoreSparks] 用户ID为空，跳过监听')
        return
    }

    // 若已监听其他用户，先清理再重建，防止用户切换时收到旧用户数据
    if (unsubscribe && listeningUserId !== userId) {
        console.debug('[主站][FirestoreSparks] 用户切换，清理旧监听')
        stopFirestoreListener()
    }

    // 避免对同一用户重复监听（正常控制流，非错误）
    if (unsubscribe) {
        console.debug('[主站][FirestoreSparks] 已在监听同一用户，跳过重复监听')
        return
    }

    try {
        const db = getFirestore(getApp(), config.firestoreDatabaseId)
        const ref = doc(db, config.firestoreCollectionWebSparksUser, userId)
        console.debug('[主站][FirestoreSparks] 开始监听', config.firestoreCollectionWebSparksUser, userId)
        listeningUserId = userId
        // 内存基线从持久化存储读取做种子；onSnapshot 在 attach（含刷新/重连）后会先回吐
        // 一次当前文档，靠下面的 createTime 时间戳去重区分「未处理的新事件」与「刷新重读的旧事件」。
        let lastSeenMillis = readLastSeen(userId)
        unsubscribe = onSnapshot(
            ref,
            (snapshot) => {
                const data = snapshot.data()
                console.debug('[主站][FirestoreSparks] 收到快照', data)
                if (!data || !data.action) {
                    return
                }

                // 按服务端 createTime 去重：仅处理比已处理过的更新的事件。
                // 拿不到时间戳时（理论上后端总会写）退化为「照常处理」，保证功能不丢。
                const ts = eventMillis(data)
                if (ts !== null) {
                    if (ts <= lastSeenMillis) {
                        console.debug('[主站][FirestoreSparks] 事件已处理过，跳过', data.action, ts)
                        return
                    }
                    lastSeenMillis = ts
                    writeLastSeen(userId, ts)
                }

                switch (data.action) {
                    case 'web_sparks_update':
                        if (typeof data.webSparks === 'number') {
                            // 直接更新 Vuex，无需 API 请求
                            store.commit('user/SET_SPARKS', {
                                webSparks: data.webSparks,
                                refreshed: true
                            })
                            console.debug('[主站][FirestoreSparks] 更新 Vuex webSparks', data.webSparks)
                        }
                        break
                    case 'vip_status_changed':
                        // 会员状态变更（如子站支付成功后由后端写入）：重新拉取权威用户/订阅信息刷新会员态。
                        console.debug('[主站][FirestoreSparks] 收到 vip_status_changed，刷新会员信息')
                        if (store.getters['user/isLoggedIn']) {
                            store.dispatch('user/refreshUserInfo')
                        }
                        break
                    // TODO: 后续扩展其他 action，例如 streak_updated 等
                    default:
                        break
                }
            },
            (error: FirestoreError) => {
                console.error('[主站][FirestoreSparks] 监听失败:', error.code, error.message)

                // 按错误码分类降级：权限/配置类错误触发一次 API 兜底刷新
                if (error.code === 'permission-denied' || error.code === 'not-found') {
                    console.warn('[FirestoreSparks] Firestore 配置异常，降级到 API 刷新')
                    if (store.getters['user/isLoggedIn']) {
                        store.dispatch('user/refreshUserInfo')
                    }
                }
            }
        )
    } catch (e) {
        console.error('[主站][FirestoreSparks] 启动监听失败:', e)
    }
}

/**
 * 停止 Firestore Sparks 监听（登出或初始化前调用）
 */
export function stopFirestoreListener() {
    if (unsubscribe) {
        unsubscribe()
        unsubscribe = null
    }
    listeningUserId = null
}
